"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * @module Utils
 */
var helpers_1 = require("./helpers");
var crypt_1 = require("./crypt");
var chain_1 = require("./api/chain");
var dictionary_1 = require("./resources/dictionary");
var BigInteger = require("big-integer");
var js_sha512_1 = require("js-sha512");
var asset_1 = require("./model/asset");
var validator_1 = require("./modules/validator");
var types_1 = require("./model/types");
var utils_1 = require("./model/utils");
var Utils = /** @class */ (function () {
    function Utils() {
    }
    /**
     * Format DCT price amount from blockchain format to real and readable formatted string.
     *
     * Example: Amount price from blockchain is 1, formatted price 0.00000001 DCT
     *
     * @param {number} dctAmount    Amount of DCT asset.
     * @return {string}             Formatted amount in string format.
     */
    Utils.formatToReadiblePrice = function (dctAmount) {
        return (dctAmount / chain_1.ChainApi.DCTPower).toFixed(8);
    };
    /**
     * Formats amount to DCT precision.
     * Value is devided by asset's precision factor
     * Note: Most of amount values are already formatted for this precision format.
     *
     * @param {number} amount   Amount to be formatted.
     * @returns {number}        DCore formatted amount.
     */
    Utils.formatAmountForDCTAsset = function (amount) {
        return amount / chain_1.ChainApi.DCTPower;
    };
    /**
     * Formats amount to format with decimal numbers.
     * Note: Most of amount values are already formatted for this precision format.
     *
     * @param {number} amount               Amount of asset in DCore network format.
     * @param {DCoreAssetObject} asset      Asset object to format amount to.
     * @returns {number}                    Formatted number in format with decimal numbers.
     */
    Utils.formatAmountForAsset = function (amount, asset) {
        return amount / Math.pow(10, asset.precision);
    };
    /**
     * Format amount value for DCore, to format without decimal numbers.
     *
     * @param {number} amount           Amount with decimal numbers to format.
     * @param {DCoreAssetObject} asset  Asset object for formatting.
     * @returns {number}                Formatted number.
     */
    Utils.formatAmountToAsset = function (amount, asset) {
        var transformedAmount = amount * Math.pow(10, asset.precision);
        return Number(transformedAmount.toFixed(0));
    };
    /**
     * RIPEMD 160 hash
     *
     * @param {Buffer} fromBuffer       Buffer to calculate hash from.
     * @returns {string}                RIPEMD160 hash.
     */
    Utils.ripemdHash = function (fromBuffer) {
        return crypt_1.CryptoUtils.ripemdHash(fromBuffer);
    };
    /**
     * Generate private and public key from given brain key.
     *
     * @param {string} fromBrainKey                                     Brain key to generate keys from.
     * @return {any[]} [privateKey: KeyPrivate, publicKey: KeyPublic]   Keys.
     */
    Utils.generateKeys = function (fromBrainKey) {
        var normalizedBk = Utils.normalize(fromBrainKey);
        var pkey = utils_1.KeyPrivate.fromBrainKey(normalizedBk);
        var pubKey = utils_1.KeyPublic.fromPrivateKey(pkey);
        return [pkey, pubKey];
    };
    /**
     * Calculate public key from given private key.
     *
     * @param {string} privateKey      Private key to get public key for.
     * @return {KeyPublic}              KeyPublic object.
     */
    Utils.getPublicKey = function (privateKey) {
        var pkey = utils_1.KeyPrivate.fromWif(privateKey);
        return pkey.getPublicKey();
    };
    /**
     * Create KeyPrivate object from WIF format of private key.
     *
     * @param {string} pkWif    Private key in WIF(hex) (Wallet Import Format) format.
     * @return {KeyPrivate}     KeyPrivate object.
     */
    Utils.privateKeyFromWif = function (pkWif) {
        try {
            return utils_1.KeyPrivate.fromWif(pkWif);
        }
        catch (exception) {
            throw new Error(utils_1.UtilsError.wrong_private_key);
        }
    };
    /**
     * Create KeyPublic object from string format of public key.
     *
     * @param {string} pubKeyString     Public key string.
     * @return {KeyPublic}              KeyPublic object.
     */
    Utils.publicKeyFromString = function (pubKeyString) {
        return utils_1.KeyPublic.fromString(pubKeyString);
    };
    /**
     * Get random brain key.
     * https://docs.decent.ch/developer/group___wallet_a_p_i___account.html#ga4841362854805ef897b8415eb8866424
     *
     * @returns {string}    Brain key string.
     */
    Utils.suggestBrainKey = function () {
        return helpers_1.dcorejs_lib.key.suggest_brain_key(dictionary_1.dictionary.en.join(','));
    };
    /**
     * Get brainkey info with brain key, private key and public key.
     * https://docs.decent.ch/developer/group___wallet_a_p_i___account.html#ga1cca4c087c272e07681b2c6d203b7d74
     *
     * @param {string} brainKey     Brain keys string.
     * @returns {BrainKeyInfo}      BrainKeyInfo object.
     */
    Utils.getBrainKeyInfo = function (brainKey) {
        var normalizedBK = Utils.normalize(brainKey);
        var keys = Utils.generateKeys(normalizedBK);
        var result = {
            brain_priv_key: normalizedBK,
            pub_key: keys[1].stringKey,
            wif_priv_key: keys[0].stringKey
        };
        return result;
    };
    /**
     * Normalize brain key for further usage in Utils's methods
     *
     * @param {string} brainKey         Brain key generated from Utils.suggestBrainKey or from wallet CLI
     * @returns {string}                Normalized brain key
     */
    Utils.normalize = function (brainKey) {
        if (typeof brainKey !== types_1.Type.string) {
            throw new Error('string required for brainKey');
        }
        brainKey = brainKey.trim();
        brainKey = brainKey.toUpperCase();
        return brainKey.split(/[\t\n\v\f\r ]+/).join(' ');
    };
    /**
     * Generate random
     * @returns {string}
     */
    Utils.generateNonce = function () {
        return helpers_1.dcorejs_lib.TransactionHelper.unique_nonce_uint64();
    };
    /**
     * Generates El Gamal public key from given El Gamal private key
     *
     * @param {string} elGamalPrivate   El Gamal private key string.
     * @returns {string}                ElGamal public key string.
     */
    Utils.elGamalPublic = function (elGamalPrivate) {
        var elgPriv = BigInteger(elGamalPrivate);
        var modulus = BigInteger('11760620558671662461946567396662025495126946227619472274' +
            '601251081547302009186313201119191293557856181195016058359990840577430081932807832465057884143546419');
        var generator = BigInteger(3);
        return generator.modPow(elgPriv, modulus).toString();
    };
    /**
     * Generates El Gamal key for content exchange from given private key WIF string
     *
     * @param {string} privateKeyWif        WIF formatted private key of account for which generating El Gamal key
     * @returns {string}                    El Gamal private key string.
     */
    Utils.elGamalPrivate = function (privateKeyWif) {
        try {
            var pKey = Utils.privateKeyFromWif(privateKeyWif);
            var hash = js_sha512_1.sha512(pKey.key.d.toBuffer());
            return BigInteger(hash, 16).toString();
        }
        catch (exception) {
            throw new Error(utils_1.UtilsError.wrong_private_key);
        }
    };
    /**
     * Calculate El Gamal keys pair from WIF private key
     * @param {string} privateKeyWif    WIF formatted private key of account for which generating El Gamal keys
     * @returns {ElGamalKeys}           ElGamalKeys object.
     */
    Utils.generateElGamalKeys = function (privateKeyWif) {
        try {
            return utils_1.ElGamalKeys.generate(privateKeyWif);
        }
        catch (exception) {
            throw new Error(utils_1.UtilsError.wrong_private_key);
        }
    };
    /**
     * Generate random brain key and El Gamal keys from brain key.
     *
     * @returns {[BrainKeyInfo , ElGamalKeys]}
     */
    Utils.generateBrainKeyElGamalKey = function () {
        var brainKey = Utils.suggestBrainKey();
        var bkInfo = Utils.getBrainKeyInfo(brainKey);
        var elGamalKeys = Utils.generateElGamalKeys(bkInfo.wif_priv_key);
        return [bkInfo, elGamalKeys];
    };
    /**
     * Calculate derived private key apart from primary(with sequence number 0).
     * NOTE: May be used as additional keys when creating account - owner, memo key
     *
     * @param {string} brainKey     Brain key string.
     * @param {number} sequence     Sequence number to derive private key from it. If selected 0, primary private key is generated.
     * @returns {KeyPrivate}        KeyPrivate object.
     */
    Utils.derivePrivateKey = function (brainKey, sequence) {
        return utils_1.KeyPrivate.fromBrainKey(brainKey, sequence);
    };
    __decorate([
        validator_1.Validate(types_1.Type.number),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Number]),
        __metadata("design:returntype", String)
    ], Utils, "formatToReadiblePrice", null);
    __decorate([
        validator_1.Validate(types_1.Type.number),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Number]),
        __metadata("design:returntype", Number)
    ], Utils, "formatAmountForDCTAsset", null);
    __decorate([
        validator_1.Validate(types_1.Type.number, asset_1.DCoreAssetObject),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Number, asset_1.DCoreAssetObject]),
        __metadata("design:returntype", Number)
    ], Utils, "formatAmountForAsset", null);
    __decorate([
        validator_1.Validate(types_1.Type.number, asset_1.DCoreAssetObject),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Number, asset_1.DCoreAssetObject]),
        __metadata("design:returntype", Number)
    ], Utils, "formatAmountToAsset", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", String)
    ], Utils, "ripemdHash", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", Array)
    ], Utils, "generateKeys", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", utils_1.KeyPublic)
    ], Utils, "getPublicKey", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", utils_1.KeyPrivate)
    ], Utils, "privateKeyFromWif", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", utils_1.KeyPublic)
    ], Utils, "publicKeyFromString", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", Object)
    ], Utils, "getBrainKeyInfo", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", String)
    ], Utils, "normalize", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", String)
    ], Utils, "elGamalPublic", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", String)
    ], Utils, "elGamalPrivate", null);
    __decorate([
        validator_1.Validate(types_1.Type.string),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", utils_1.ElGamalKeys)
    ], Utils, "generateElGamalKeys", null);
    __decorate([
        validator_1.Validate(types_1.Type.string, types_1.Type.number),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String, Number]),
        __metadata("design:returntype", utils_1.KeyPrivate)
    ], Utils, "derivePrivateKey", null);
    return Utils;
}());
exports.Utils = Utils;
//# sourceMappingURL=utils.js.map


//////////////////
// WEBPACK FOOTER
// ./lib/utils.js
// module id = 20
// module chunks = 0